import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Loader2, LogOut, ShieldCheck } from 'lucide-react';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import SelectiveAccountStatus from '@/components/auth/SelectiveAccountStatus.jsx';

function DesktopAccountMenu({
    user,
    t,
    displayName,
    avatarContainerClasses,
    avatarContent,
    isAccountMenuOpen,
    onToggleAccountMenu,
    onSignOut,
    isSigningOut,
    accountMenuRef,
    onNavigateToSignup,
    onNavigateToLogin,
    onSelectAccountPrivacy,
    showSelectiveAccountStatus = false,
}) {
    return (
        <div className="hidden xl:flex items-center" ref={accountMenuRef}>
            {user ? (
                <div className="relative">
                    <button
                        type="button"
                        onClick={onToggleAccountMenu}
                        className={cn(
                            buttonVariants({ variant: 'ghost', size: 'sm' }),
                            'text-white/90 hover:bg-white/10 flex items-center gap-2'
                        )}
                    >
                        <div className={avatarContainerClasses}>{avatarContent}</div>
                        <span className="hidden xl:inline text-left">{displayName || t('auth.account')}</span>
                        <ChevronDown className={`h-4 w-4 transition-transform ${isAccountMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isAccountMenuOpen && (
                        <div className={cn(
                            'absolute right-0 mt-2 rounded-md border border-white/10 bg-[var(--glass-bg)] p-3 shadow-lg backdrop-blur',
                            showSelectiveAccountStatus ? 'w-72' : 'w-56',
                        )}>
                            {showSelectiveAccountStatus ? (
                                <SelectiveAccountStatus user={user} className="mb-3" />
                            ) : (
                                <div className="mb-3 text-sm text-white/70">
                                    {displayName ? t('auth.welcome_user', { name: displayName }) : t('auth.account')}
                                </div>
                            )}
                            <Link
                                to="/account/privacy"
                                onClick={onSelectAccountPrivacy}
                                className="mb-1 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-white/80 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200/70 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                            >
                                <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                                <span>{t('account_privacy.title')}</span>
                            </Link>
                            <button
                                type="button"
                                onClick={onSignOut}
                                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-red-400 transition hover:bg-red-400/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                                disabled={isSigningOut}
                            >
                                {isSigningOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
                                <span>{t('auth.logout')}</span>
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onNavigateToSignup}
                        className={cn(
                            buttonVariants({ variant: 'default', size: 'sm' }),
                            'bg-[var(--primary)] text-white hover:bg-[var(--primary)]/80'
                        )}
                    >
                        {t('auth.sign_up')}
                    </button>
                    <button
                        type="button"
                        onClick={onNavigateToLogin}
                        className={cn(
                            buttonVariants({ variant: 'outline', size: 'sm' }),
                            'border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/20'
                        )}
                    >
                        {t('auth.login')}
                    </button>
                </div>
            )}
        </div>
    );
}

export default DesktopAccountMenu;
